import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const soundAlertRef = useRef();
	const alertAudioRef = useRef(null);
	const originalTitleRef = useRef(document.title || "Flowch");

	const historyRef = useRef(history);
	const pushSyncInProgressRef = useRef(false);

	const hasNotificationApi = () =>
		typeof window !== "undefined" && "Notification" in window;

	const getNotificationPermission = () => {
		if (!hasNotificationApi()) {
			return "denied";
		}

		try {
			return Notification.permission;
		} catch (_err) {
			return "denied";
		}
	};

	const requestNotificationPermissionSafe = async () => {
		if (!hasNotificationApi() || typeof Notification.requestPermission !== "function") {
			return "denied";
		}

		try {
			const requestResult = Notification.requestPermission();

			if (requestResult && typeof requestResult.then === "function") {
				return await requestResult;
			}

			return getNotificationPermission();
		} catch (_err) {
			return "denied";
		}
	};

	const urlBase64ToUint8Array = base64String => {
		const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
		const base64 = (base64String + padding)
			.replace(/-/g, "+")
			.replace(/_/g, "/");
		const rawData = window.atob(base64);
		const outputArray = new Uint8Array(rawData.length);

		for (let i = 0; i < rawData.length; i += 1) {
			outputArray[i] = rawData.charCodeAt(i);
		}

		return outputArray;
	};

	const syncWebPushSubscription = async () => {
		if (
			pushSyncInProgressRef.current ||
			!("serviceWorker" in navigator) ||
			!("PushManager" in window) ||
			!hasNotificationApi() ||
			getNotificationPermission() !== "granted" ||
			!user?.id
		) {
			return;
		}

		pushSyncInProgressRef.current = true;
		try {
			const registration = await navigator.serviceWorker.register("/sw.js");
			const keyResponse = await api.get("/push-subscriptions/public-key");
			const publicKey = keyResponse?.data?.publicKey;

			if (!publicKey) {
				return;
			}

			let subscription = await registration.pushManager.getSubscription();
			if (!subscription) {
				subscription = await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: urlBase64ToUint8Array(publicKey),
				});
			}

			await api.post("/push-subscriptions", {
				subscription: subscription.toJSON(),
			});
		} catch (_error) {
			// Keep notifications feature resilient when push setup fails.
		} finally {
			pushSyncInProgressRef.current = false;
		}
	};

	const toNumberOrNull = value => {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	};

	useEffect(() => {
		if (!alertAudioRef.current) {
			alertAudioRef.current = new Audio(alertSound);
			alertAudioRef.current.preload = "auto";
		}

		soundAlertRef.current = () => {
			const audio = alertAudioRef.current;
			if (!audio) {
				return;
			}

			audio.currentTime = 0;
			const playback = audio.play();
			if (playback && typeof playback.catch === "function") {
				playback.catch(() => {});
			}
		};

		if (!hasNotificationApi()) {
			console.log("This browser doesn't support notifications");
			return;
		} else if (getNotificationPermission() === "default") {
			requestNotificationPermissionSafe().then(permission => {
				if (permission === "granted") {
					void syncWebPushSubscription();
				}
			});
			return;
		}

		if (getNotificationPermission() === "granted") {
			void syncWebPushSubscription();
		}
	}, [user?.id]);

	useEffect(() => {
		if (getNotificationPermission() === "granted") {
			void syncWebPushSubscription();
		}
	}, [user?.id]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		const count = notifications.length;
		document.title = count > 0
			? `(${count}) ${originalTitleRef.current}`
			: originalTitleRef.current;

		return () => {
			document.title = originalTitleRef.current;
		};
	}, [notifications.length]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", data => {
			const ticketUserId = toNumberOrNull(data?.ticket?.userId);
			const currentUserId = toNumberOrNull(user?.id);
			const isParticipant = Array.isArray(data?.ticket?.participants)
				? data.ticket.participants.some(
						participant => toNumberOrNull(participant?.userId) === currentUserId
					)
				: false;
			const isAssignedToCurrentUser =
				ticketUserId === null ||
				currentUserId === null ||
				ticketUserId === currentUserId ||
				isParticipant;

			if (
				data.action === "create" &&
				!data.message.read &&
				isAssignedToCurrentUser
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					!isAssignedToCurrentUser ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		socket.on("ticketInternalNotification", data => {
			const currentUserId = toNumberOrNull(user?.id);
			const targetUserIds = Array.isArray(data?.userIds)
				? data.userIds.map(toNumberOrNull).filter(Boolean)
				: [];

			if (!currentUserId || !targetUserIds.includes(currentUserId) || !data?.ticket) {
				return;
			}

			setNotifications(prevState => {
				const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
				if (ticketIndex !== -1) {
					prevState[ticketIndex] = data.ticket;
					return [...prevState];
				}
				return [data.ticket, ...prevState];
			});

			const shouldNotNotificate =
				(data.ticketId === ticketIdRef.current && document.visibilityState === "visible");

			if (shouldNotNotificate) return;

			handleInternalNotification(data);
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleInternalNotification = data => {
		if (soundAlertRef.current) {
			soundAlertRef.current();
		}

		if (!hasNotificationApi() || getNotificationPermission() !== "granted") {
			return;
		}

		const ticket = data.ticket;
		const title = data.title || i18n.t("messagesList.internalEvents.types.internal_note_added");
		const body = data.body || i18n.t("messagesList.internalEvents.types.internal_note_added");

		const options = {
			body: `${body} - ${format(new Date(), "HH:mm")}`,
			icon: data.contact?.profilePicUrl,
			tag: `internal-${ticket.id}`,
			renotify: true,
		};

		let notification;
		try {
			notification = new Notification(title, options);
		} catch (_error) {
			return;
		}

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
	};

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		if (soundAlertRef.current) {
			soundAlertRef.current();
		}

		if (!hasNotificationApi() || getNotificationPermission() !== "granted") {
			return;
		}

		const options = {
			body: `${message.body || "Nova mensagem"} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		let notification;
		try {
			notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact.name}`,
				options
			);
		} catch (_error) {
			return;
		}

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
